import React, { useEffect, useMemo, useState } from "react";
import StatusBadge from "@/components/StatusBadge";
import { RowActions } from "@/components/RowActions";
import { exportToCSV } from "@/lib/csv";
import { DownloadSimpleIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { filterPvaRows, natKey, productOf, pvaTotals } from "@/lib/pvaRows";

// PVA-MACHINE-PLAN-01 · one row per machine-plan allocation (exact plan id + WO).
const PAGE = 25;
const STATUSES = ["Planned", "Running", "Paused", "Completed"];
const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const inpCls = "h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";

const TIMING_TONE = {
  "Not Started": "text-zinc-300 bg-zinc-500/10 border-zinc-500/30",
  "Within Schedule": "text-sky-300 bg-sky-500/10 border-sky-500/30",
  "Overdue": "text-red-300 bg-red-500/10 border-red-500/30",
  "Completed Early": "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  "Completed On Time": "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  "Completed Late": "text-amber-300 bg-amber-500/10 border-amber-500/30",
  "Actual Finish Unavailable": "text-zinc-400 bg-zinc-500/10 border-zinc-500/30",
};

function Totals({ t }) {
  const cells = [
    ["Machines", t.machines, "pva-total-machines", "text-white"],
    ["Plans", t.plans, "pva-total-plans", "text-white"],
    ["Allocated", `${fmt(t.pln)} m`, "pva-total-allocated", "text-white"],
    ["Produced", `${fmt(t.prd)} m`, "pva-total-produced", "text-emerald-300"],
    ["Balance", `${fmt(t.bal)} m`, "pva-total-balance", "text-amber-300"],
    ["Excess Produced", `${fmt(t.exc)} m`, "pva-total-excess", "text-rose-300"],
  ];
  return (
    <div data-testid="pva-filtered-totals" className="tactical-card px-4 py-3 border-l-2 border-amber-400/60">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Filtered Plan Totals · all matching rows</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cells.map(([l, v, id, c]) => (
          <div key={id}><div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div data-testid={id} className={`font-mono text-lg font-bold tabular-nums ${c}`}>{v}</div></div>
        ))}
      </div>
    </div>
  );
}

function Filters({ f, setF, machines, onClear, onCsv, cols, setCols }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="relative">
        <MagnifyingGlassIcon size={14} className="absolute left-2.5 top-2.5 text-zinc-500" />
        <input data-testid="pva-search" value={f.search} onChange={set("search")} placeholder="WO, Plan, SO/ESO, Product, Customer, Machine…"
          className={`${inpCls} pl-8 w-[380px]`} />
      </div>
      <label className="text-[10px] text-zinc-500 uppercase tracking-widest">From<input type="date" data-testid="pva-date-from" value={f.from} onChange={set("from")} className={`${inpCls} block`} /></label>
      <label className="text-[10px] text-zinc-500 uppercase tracking-widest">To<input type="date" data-testid="pva-date-to" value={f.to} onChange={set("to")} className={`${inpCls} block`} /></label>
      <select data-testid="pva-machine-filter" value={f.machine} onChange={set("machine")} className={inpCls}>
        <option value="">All machines</option>
        {machines.map(m => <option key={m.id} value={m.id}>{m.code}</option>)}
      </select>
      <select data-testid="pva-status-filter" value={f.status} onChange={set("status")} className={inpCls}>
        <option value="">All statuses</option>
        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <button data-testid="pva-clear-filters" onClick={onClear} className="h-9 px-3 border border-[var(--border)] text-xs text-zinc-300 rounded-sm hover:bg-[var(--bg-2)]">Clear Filters</button>
      <label className="h-9 px-2 flex items-center gap-1.5 text-xs text-zinc-400"><input type="checkbox" data-testid="pva-col-so" checked={cols.so} onChange={e => setCols({ ...cols, so: e.target.checked })} />SO/ESO</label>
      <label className="h-9 px-2 flex items-center gap-1.5 text-xs text-zinc-400"><input type="checkbox" data-testid="pva-col-customer" checked={cols.customer} onChange={e => setCols({ ...cols, customer: e.target.checked })} />Customer</label>
      <button data-testid="export-plan-csv" onClick={onCsv} className="ml-auto h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
        <DownloadSimpleIcon size={14} weight="bold" />Export CSV
      </button>
    </div>
  );
}

const S1 = "sticky left-0 z-10 bg-[var(--bg-1)] w-[110px] min-w-[110px]";
const S2 = "sticky left-[110px] z-10 bg-[var(--bg-1)] w-[150px] min-w-[150px]";
const S3 = "sticky left-[260px] z-10 bg-[var(--bg-1)] w-[160px] min-w-[160px] shadow-[1px_0_0_var(--border)]";

function Row({ r, i, cols, actions }) {
  const pct = r.progress_pct;
  return (
    <tr className="border-b border-[var(--border)]" data-testid={`plan-row-${i}`}>
      <td className={`${S1} px-3 py-2.5 font-mono text-white`} data-testid={`plan-machine-${i}`}>{r.machine_code || "—"}</td>
      <td className={`${S2} px-3 py-2.5`} data-testid={`plan-queue-${i}`}>
        <span className={`text-xs font-semibold ${r.queue_label === "Done" ? "text-zinc-500" : r.queue_label === "First" ? "text-emerald-300" : "text-sky-300"}`}>{r.queue_label || "—"}</span>
        {(r.predecessor_wo_nos || []).length > 0 && (
          <div className="text-[10px] text-zinc-500 font-mono" data-testid={`plan-predecessor-${i}`}>after {r.predecessor_wo_nos.join(", ")}</div>
        )}
      </td>
      <td className={`${S3} px-3 py-2.5 font-mono whitespace-nowrap`} data-testid={`plan-doc-no-${i}`}>
        {r.plan_no ? <span className="text-white">{r.plan_no}</span> : <span className="text-zinc-500 italic text-xs">Legacy / Not Assigned</span>}
      </td>
      <td className="px-3 py-2.5 font-mono text-emerald-300 whitespace-nowrap" data-testid={`plan-wo-no-${i}`}>{r.source_wo_no || "—"}</td>
      <td className="px-3 py-2.5 font-mono text-zinc-300 text-xs max-w-[260px] truncate" title={productOf(r)} data-testid={`plan-product-${i}`}>{productOf(r) || "—"}</td>
      {cols.so && <td className="px-3 py-2.5 font-mono text-white whitespace-nowrap" data-testid={`plan-order-${i}`}>{r.order_no || "—"}</td>}
      {cols.customer && <td className="px-3 py-2.5 text-zinc-400" data-testid={`plan-customer-${i}`}>{r.customer || "—"}</td>}
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-white" data-testid={`plan-planned-${i}`}>{fmt(r.planned_qty)}</td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-emerald-300" data-testid={`plan-produced-${i}`}>{fmt(r.produced_qty)}</td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-amber-300" data-testid={`plan-balance-qty-${i}`}>
        {fmt(r.plan_balance_qty)}
        {+r.excess_qty > 0 && <div className="text-[10px] text-rose-300" data-testid={`plan-excess-${i}`}>+{fmt(r.excess_qty)} excess</div>}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-300" data-testid={`plan-progress-${i}`}>{pct === null || pct === undefined ? "—" : `${pct}%`}</td>
      <td className="px-3 py-2.5" data-testid={`plan-exec-status-${i}`}><StatusBadge status={r.execution_status} /></td>
      <td className="px-3 py-2.5 font-mono text-xs text-zinc-400 whitespace-nowrap" data-testid={`plan-start-${i}`}>{r.planned_start || "—"}</td>
      <td className="px-3 py-2.5 font-mono text-xs text-zinc-400 whitespace-nowrap" data-testid={`plan-finish-${i}`}>{r.planned_finish_baseline || "—"}</td>
      <td className="px-3 py-2.5 font-mono text-xs text-zinc-300 whitespace-nowrap" data-testid={`plan-actual-start-${i}`}>{r.actual_start || "—"}</td>
      <td className="px-3 py-2.5 font-mono text-xs text-zinc-300 whitespace-nowrap" data-testid={`plan-actual-finish-${i}`}>{r.actual_finish || "—"}</td>
      <td className="px-3 py-2.5 whitespace-nowrap" data-testid={`plan-timing-${i}`}>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border uppercase tracking-wider ${TIMING_TONE[r.timing_status] || ""}`}>{r.timing_status}</span>
      </td>
      <td className="px-3 py-2.5 text-right"><RowActions testid={`plan-actions-${i}`} items={actions(r, i)} /></td>
    </tr>
  );
}

const EMPTY = { search: "", from: "", to: "", machine: "", status: "" };

export default function PlanVsActualTable({ rows, machines, actions }) {
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(0);
  const [cols, setCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pva_cols")) || { so: false, customer: false }; } catch { return { so: false, customer: false }; }
  });
  useEffect(() => { localStorage.setItem("pva_cols", JSON.stringify(cols)); }, [cols]);
  useEffect(() => { setPage(0); }, [f]);
  const filtered = useMemo(() => filterPvaRows(rows, f), [rows, f]);
  const t = useMemo(() => pvaTotals(filtered), [filtered]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageRows = filtered.slice(page * PAGE, page * PAGE + PAGE);
  const usedMachines = useMemo(() => {
    const ids = new Set(rows.map(r => r.machine_id));
    return machines.filter(m => ids.has(m.id)).sort((a, b) => natKey(a.code).localeCompare(natKey(b.code)));
  }, [rows, machines]);

  const csv = () => {
    const columns = [
      { key: "machine_code", label: "Machine" }, { key: "queue_label", label: "Queue" },
      { key: "predecessor", label: "Preceding WO" }, { key: "plan_no", label: "Plan No." },
      { key: "source_wo_no", label: "WO No." }, { key: "product", label: "Product" },
      ...(cols.so ? [{ key: "order_no", label: "SO/ESO" }] : []),
      ...(cols.customer ? [{ key: "customer", label: "Customer" }] : []),
      { key: "planned_qty", label: "Planned Qty" }, { key: "produced_qty", label: "Produced Qty" },
      { key: "plan_balance_qty", label: "Balance Qty" }, { key: "excess_qty", label: "Excess Qty" },
      { key: "progress", label: "Progress %" }, { key: "execution_status", label: "Execution Status" },
      { key: "planned_start", label: "Planned Start" }, { key: "planned_finish_baseline", label: "Planned Finish" },
      { key: "actual_start", label: "Actual Start" }, { key: "actual_finish", label: "Actual Finish" },
      { key: "timing_status", label: "Timing Status" },
    ];
    const data = filtered.map(r => ({ ...r, product: productOf(r), predecessor: (r.predecessor_wo_nos || []).join(" | "),
      progress: r.progress_pct === null || r.progress_pct === undefined ? "—" : r.progress_pct }));
    data.push({ machine_code: `TOTAL · ${t.machines} machine(s)`, plan_no: `${t.plans} plan(s)`, planned_qty: t.pln,
      produced_qty: t.prd, plan_balance_qty: t.bal, excess_qty: t.exc });
    exportToCSV("plan-vs-actual.csv", data, columns);
  };

  const head = ["WO No.", "Product", ...(cols.so ? ["SO/ESO"] : []), ...(cols.customer ? ["Customer"] : []),
    "Planned Qty", "Produced Qty", "Balance Qty", "Progress", "Execution Status", "Planned Start", "Planned Finish",
    "Actual Start", "Actual Finish", "Timing Status", "Actions"];
  const right = new Set(["Planned Qty", "Produced Qty", "Balance Qty", "Progress", "Actions"]);
  return (
    <div className="space-y-3" data-testid="pva-machine-plan-view">
      <Filters f={f} setF={setF} machines={usedMachines} onClear={() => setF(EMPTY)} onCsv={csv} cols={cols} setCols={setCols} />
      <Totals t={t} />
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className={`${S1} text-left px-3 py-3 font-medium`}>Machine</th>
              <th className={`${S2} text-left px-3 py-3 font-medium`}>Queue</th>
              <th className={`${S3} text-left px-3 py-3 font-medium`}>Plan No.</th>
              {head.map(h => <th key={h} className={`${right.has(h) ? "text-right" : "text-left"} px-3 py-3 font-medium whitespace-nowrap`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => <Row key={r.plan_id || i} r={r} i={page * PAGE + i} cols={cols} actions={actions} />)}
            {filtered.length === 0 && <tr><td colSpan={head.length + 3} className="text-center py-16 text-zinc-500" data-testid="pva-empty">{rows.length ? "No plans match the current search and filters." : "No plans yet — click New Plan or use Auto Allocation to create one."}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-zinc-500" data-testid="pva-pagination">
        <span data-testid="pva-page-info">Showing {filtered.length ? page * PAGE + 1 : 0}–{Math.min(filtered.length, (page + 1) * PAGE)} of {filtered.length}</span>
        <div className="flex gap-2">
          <button data-testid="pva-prev-page" disabled={page === 0} onClick={() => setPage(page - 1)} className="h-8 px-3 border border-[var(--border)] rounded-sm disabled:opacity-40">Prev</button>
          <span className="h-8 px-2 flex items-center">Page {page + 1} / {pages}</span>
          <button data-testid="pva-next-page" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="h-8 px-3 border border-[var(--border)] rounded-sm disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
